import { useState } from 'react'
import { RadioTower, RefreshCw } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import { Button } from '@/components/ui/button'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'
import {
  useFlightAwareStatus,
  useFlightAwareSync,
  FLIGHTAWARE_HORIZON_HOURS,
} from '@/hooks/useFlightAware'
import { FlightAwareSyncDrawer } from './FlightAwareSyncDrawer'
import type { FlightAwareSyncResult } from '@/lib/api/flightaware'

interface FlightAwareSyncButtonProps {
  locationId: string | null
}

/**
 * Manual "Sync FlightAware" button for the Today board. Only renders when the
 * FlightAware integration is enabled for the active location. On click it runs
 * one AeroAPI sync (enrich existing schedules + list new airport flights) over
 * the next FLIGHTAWARE_HORIZON_HOURS and opens a results drawer. Shows the last
 * successful sync time beside the button.
 */
export function FlightAwareSyncButton({ locationId }: FlightAwareSyncButtonProps) {
  const { enabled, lastSync } = useFlightAwareStatus(locationId)
  const sync = useFlightAwareSync()
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [result, setResult] = useState<FlightAwareSyncResult | null>(null)

  if (!enabled || !locationId) return null

  const handleSync = () => {
    sync.mutate(
      { locationId, horizonHours: FLIGHTAWARE_HORIZON_HOURS },
      {
        onSuccess: (res) => {
          setResult(res)
          setDrawerOpen(true)
        },
      },
    )
  }

  return (
    <>
      <div className="flex items-center gap-1.5">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button variant="outline" size="sm" onClick={handleSync} disabled={sync.isPending}>
                {sync.isPending ? (
                  <RefreshCw className="h-4 w-4 mr-1.5 animate-spin" />
                ) : (
                  <RadioTower className="h-4 w-4 mr-1.5" />
                )}
                Sync FlightAware
              </Button>
            </TooltipTrigger>
            <TooltipContent side="top">
              <p className="text-xs">Fetches arrivals &amp; departures for the next {FLIGHTAWARE_HORIZON_HOURS}h</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        {lastSync && !sync.isPending && (
          <span className="text-xs text-muted-foreground hidden sm:inline">
            synced {formatDistanceToNow(new Date(lastSync), { addSuffix: true })}
          </span>
        )}
      </div>

      <FlightAwareSyncDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        locationId={locationId}
        result={result}
      />
    </>
  )
}
